import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getModule } from "store/userSlice";
import { fetchGeneralSettings, getGeneralSettings } from "store/generalSettingsSlice";
import { useForm } from "react-hook-form";
import { toast } from "helpers/toast";
import { errorMessage, scrollInto } from "helpers/common";
import * as Sentry from "@sentry/react";
import { api } from 'helpers/axios';
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { isDbModule } from "helpers/module";
import { Button, DateInput, Field, Input, InputAffix, NativeSelect } from "components/ui";

export default function General() {
  const dispatch = useDispatch();
  const { translate, editableTranslate } = useTranslation();

  const settings = useSelector(getGeneralSettings);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedSettings, setGroupedSettings] = useState({});
  const [visibleSettings, setVisibleSettings] = useState({});

  const currentModule = useSelector(getModule);

  const { register, setValue, getValues, watch } = useForm();

  useEffect(() => {
    dispatch(fetchGeneralSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      const grouped = settings.reduce((acc, setting) => {
        if (!acc[setting.section_translation_key]) {
          acc[setting.section_translation_key] = [];
        }
        acc[setting.section_translation_key].push(setting);
        return acc;
      }, {});
      setGroupedSettings(grouped);

      settings.forEach((setting) => {
        let val = setting.value;
        if (setting.input_type === 'date') val = convertDateFormat(val);
        setValue(setting.key, val);
      });

      const currentValues = Object.fromEntries(settings.map((s) => [s.key, s.value]));
      const newVisibility = settings.reduce((acc, setting) => {
        if (!setting.dependency) {
          acc[setting.key] = true;
          return acc;
        }
        const depValue = currentValues[setting.dependency.key];
        const expected = Array.isArray(setting.dependency.value)
          ? setting.dependency.value
          : [setting.dependency.value];
        acc[setting.key] = expected.includes(depValue);
        return acc;
      }, {});
      setVisibleSettings(newVisibility);
    }
  }, [settings, setValue]);

  useEffect(() => {
    const subscription = watch((values) => {
      if (settings) {
        const newVisibility = settings.reduce((acc, setting) => {
          if (!setting.dependency) {
            acc[setting.key] = true;
            return acc;
          }
          const depValue = values[setting.dependency.key];
          const expected = Array.isArray(setting.dependency.value)
            ? setting.dependency.value
            : [setting.dependency.value];
          acc[setting.key] = expected.includes(depValue);
          return acc;
        }, {});
        setVisibleSettings(newVisibility);
      }
    });

    return () => subscription.unsubscribe();
  }, [settings, watch]);

  const handleSubmit = async () => {
    setLoading(true);
    const data = getValues();

    if (Object.keys(data).length === 0) {
      setLoading(false);
      return;
    }

    const payload = Object.entries(data).map(([key, value]) => {
      let val = value;
      if (key?.includes('date')) val = convertDateFormat(val);
      return { key, value: val };
    });

    try {
      const { data: responseData } = await api.patch('/settings/general', { settings: payload });
      const response = responseData.data;
      const hasError = response.filter(r => !r.success);

      if (hasError.length === 0) {
        dispatch(fetchGeneralSettings());
        toast.success(translate('form.success.saved'));
        setError(null);
      } else {
        let errMsg = hasError.map(item => `${item.key}: ${item.message}`).join("\n");
        setError(errMsg);
        scrollInto('.content-card');
      }
    } catch (err) {
      setError(errorMessage(err, 'save general settings'));
      scrollInto('.content-card');
      Sentry.captureException(err);
    }

    setLoading(false);
  };

  const convertDateFormat = (dateStr) => {
    // d.m.Y -> Y-m-d
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split('.');
      return `${y}-${m}-${d}`;
    }

    // Y-m-d -> d.m.Y
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}.${m}.${y}`;
    }

    return dateStr;
  }

  return (
    <div className="col-lg col-md-9 section-col-right d-flex">
      <div className="bg-white w-100 p-4 content-card">
        {error && (
          <ErrorAlert error={error} onClose={() => setError(null)} />
        )}

        <h4>{editableTranslate('global.menu.settings.general')}</h4>
        <p className="section-description">
          {editableTranslate('general_settings.description', 'Nehmen Sie hier allgemeine Einstellungen für die Anwendung vor.')}
        </p>

        {settings && settings.length === 0 && (
          <div className="alert alert-info" role="alert">
            {editableTranslate('general_settings.no_settings', 'Derzeit sind keine Einstellungen zu konfigurieren.')}
          </div>
        )}

        <div className="d-flex flex-column">
          {(() => {
            const sectionPriority = ['general_settings.section.evaluation_period'];
            const orderedSections = Object.keys(groupedSettings).sort((a, b) => {
              const aPriority = sectionPriority.indexOf(a);
              const bPriority = sectionPriority.indexOf(b);
              const resolvedAPriority = aPriority === -1 ? Number.MAX_SAFE_INTEGER : aPriority;
              const resolvedBPriority = bPriority === -1 ? Number.MAX_SAFE_INTEGER : bPriority;

              if (resolvedAPriority === resolvedBPriority) {
                return a.localeCompare(b);
              }

              return resolvedAPriority - resolvedBPriority;
            });

            return orderedSections.map((section, index) => (
              <div key={index}>
                <h5 className="mt-2">{editableTranslate((isDbModule(currentModule) ? `${section}.state` : section))}</h5>
                <hr />
                {(() => {
                  const renderedKeys = new Set();

                  return groupedSettings[section].map((setting, idx) => {
                    if (renderedKeys.has(setting.key)) {
                      return null;
                    }

                    // Render start/end date settings as a single inline range.
                    if (setting.input_type === 'date' && setting.key.endsWith('_start_date')) {
                      const baseKey = setting.key.replace(/_start_date$/, '');
                      const endKey = `${baseKey}_end_date`;
                      const endSetting = groupedSettings[section].find((s) => s.key === endKey);

                      if (endSetting) {
                        renderedKeys.add(endKey);

                        if (visibleSettings[setting.key] === false || visibleSettings[endKey] === false) {
                          return null;
                        }

                        return (
                          <div className="form-group mb-4" key={idx}>
                            <div className="d-flex align-items-center">
                              <div style={{ maxWidth: '180px' }}>
                                <DateInput {...register(setting.key)} />
                              </div>
                              <span className="mx-2">-</span>
                              <div style={{ maxWidth: '180px' }}>
                                <DateInput {...register(endSetting.key)} />
                              </div>
                            </div>
                          </div>
                        );
                      }
                    } else if (setting.input_type === 'date' && setting.key.endsWith('_end_date')) {
                      const baseKey = setting.key.replace(/_end_date$/, '');
                      const startKey = `${baseKey}_start_date`;
                      const startSetting = groupedSettings[section].find((s) => s.key === startKey);

                      if (startSetting) return null;
                    }

                    if (visibleSettings[setting.key] === false) {
                      return null;
                    }

                    const controlId = ['text', 'number', 'date', 'textarea', 'select'].includes(setting.input_type) ? `setting-${setting.key}` : undefined;

                    return (
                      <div className="form-group mb-4" key={idx}>
                        <Field label={editableTranslate(setting.translation_key)} htmlFor={controlId}>
                          {setting.help_text && (
                            <small className="form-text text-muted mb-2 d-block">
                              {editableTranslate(setting.help_text, setting.help_text)}
                            </small>
                          )}
                          <div className={`${setting.input_class || ''} ${setting.input_type === 'checkbox' ? 'd-flex align-items-center' : ''}`}>
                            {(setting.input_type === 'text' || setting.input_type === 'number') && (
                              <InputAffix
                                prefix={setting.input_prefix ? editableTranslate(setting.input_prefix, setting.input_prefix) : null}
                                suffix={setting.input_suffix ? editableTranslate(setting.input_suffix, setting.input_suffix) : null}
                              >
                                <Input
                                  id={controlId}
                                type={setting.input_type}
                                  {...register(setting.key)}
                                  maxLength={setting.input_type === 'text' ? 255 : undefined}
                                />
                              </InputAffix>
                            )}
                            {setting.input_type === 'date' && (
                              <DateInput id={controlId} {...register(setting.key)} />
                            )}
                            {setting.input_type === 'textarea' && (
                              <textarea id={controlId} className="form-control" {...register(setting.key)}></textarea>
                            )}
                            {setting.input_type === 'select' && (
                              <NativeSelect id={controlId} defaultValue={setting.value} {...register(setting.key)}>
                                {Object.keys(setting.input_options).map((key, i) => (
                                  <option key={i} value={setting.input_options[key].value}>
                                    {translate(setting.input_options[key].translation_key)}
                                  </option>
                                ))}
                              </NativeSelect>
                            )}
                            {setting.input_type === 'checkbox' && (
                              <div className="d -flex flex-column gap-2">
                                {Object.keys(setting.input_options).map((key, i) => (
                                  <div key={i} className="form-check">
                                    <input className="form-check-input" type="checkbox" id={`${setting.key}-${key}`} value={setting.input_options[key].value} data-testid={`setting-${setting.key}-${key}`} {...register(setting.key)} />
                                    <label className="form-check-label ml-2" htmlFor={`${setting.key}-${key}`}>
                                      {editableTranslate(setting.input_options[key].translation_key)}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                            {setting.input_type === 'radio' && (
                              <div className="d-flex flex-column gap-2">
                                {Object.keys(setting.input_options).map((key, i) => (
                                  <div key={i} className="form-check">
                                    <input className="form-check-input" type="radio" name={setting.key} id={`${setting.key}-${key}`} value={setting.input_options[key].value} data-testid={`setting-${setting.key}-${key}`} {...register(setting.key)} />
                                    <label className="form-check-label ml-2" htmlFor={`${setting.key}-${key}`}>
                                      {editableTranslate(setting.input_options[key].translation_key)}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </Field>
                      </div>
                    );
                  });
                })()}
              </div>
            ));
          })()}
        </div>

        {settings && settings.length > 0 && (
          <div className="settings-sticky-footer general-settings-footer">
            <hr className="mt-0" />
            <Button variant="primary" onClick={handleSubmit} loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
